import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useDialogDismiss from "../../hooks/useDialogDismiss";
import useSubscriptionStore from "../../store/subscription.store";
import useAuthStore from "../../store/auth.store";
import useWalletStore from "../../store/wallet.store";
import { listMatches } from "../../api/matches.api";
import { resendVerification } from "../../api/auth.api";
import { useNpsPrompt } from "../../hooks/useNpsPrompt";
import NpsDialog from "../../components/feedback/NpsDialog";

// 2026-06-05 — mobile-only DashboardDashboard component retired per
// founder ask. Mobile + tablet now render the same DesktopDashboard
// as the laptop view; the existing responsive Tailwind classes
// (grid-cols-1 → md:grid-cols-2 → lg:grid-cols-3 on the tile grid,
// flex-col → md:flex-row on the header) collapse the layout cleanly
// at phone widths. The visual identity (cards, accent colours, tile
// copy) is identical across viewports. MobileDashboard.jsx is left
// in the tree as dead code for one release in case we want to revert.
export default function Dashboard() {
  return <DesktopDashboard />;
}

// Inline "Email not verified" pill that doubles as a resend button.
// One click resends the verification email; the pill swaps to a "Sent —
// check inbox" confirmation. No modal, no extra page — friction-free path
// to clearing the gate now that paid features require verification.
function UnverifiedEmailBadge({ email }) {
  const [state, setState] = useState("idle"); // idle | sending | sent | err
  const handleClick = async () => {
    if (!email || state === "sending" || state === "sent") return;
    setState("sending");
    try {
      await resendVerification(email);
      setState("sent");
    } catch {
      setState("err");
    }
  };
  if (state === "sent") {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 font-medium">
        <span className="text-base leading-none">✓</span>
        Verification email sent — check inbox
      </span>
    );
  }
  return (
    <button
      onClick={handleClick}
      disabled={!email || state === "sending"}
      title={email ? "Resend verification email" : "Email missing — sign out and back in"}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500/20 text-amber-200 border border-amber-400/60 hover:bg-amber-500/30 hover:border-amber-400 font-medium shadow-sm transition disabled:opacity-60 cursor-pointer"
    >
      <span className="text-base leading-none">✉</span>
      {state === "sending"
        ? "Sending…"
        : state === "err"
          ? "Resend failed — click to retry"
          : "Email not verified · Resend"}
      <span className="opacity-60 text-xs">→</span>
    </button>
  );
}

function DesktopDashboard() {
  const { mySubscription, fetchMySubscription, loading } = useSubscriptionStore();
  const { user } = useAuthStore();
  const walletBalance = useWalletStore((s) => s.balance);
  const fetchBalance = useWalletStore((s) => s.fetchBalance);

  useEffect(() => {
    fetchMySubscription();
    fetchBalance();
  }, [fetchMySubscription, fetchBalance]);

  // Fetch the user's match count once so the dashboard knows whether
  // they're a true first-time user (show getting-started) or have
  // already played around (show tile grid). Just count, no payload.
  const [matchCount, setMatchCount] = useState(null);
  useEffect(() => {
    listMatches(1, { limit: 1 })
      .then((r) => setMatchCount(r.data?.count ?? r.data?.results?.length ?? 0))
      .catch(() => setMatchCount(0));
  }, []);

  // T205: Persona-1 scorer hero card data. Fetch recent matches once
  // so the hero can show: "resume last match" CTA, today's match
  // count, current 7-day streak. Lightweight — capped at 20 entries.
  const [recentMatches, setRecentMatches] = useState(null);
  useEffect(() => {
    listMatches(1, { limit: 20 })
      .then((r) => setRecentMatches(r.data?.results || []))
      .catch(() => setRecentMatches([]));
  }, []);

  // T211: NPS auto-prompt at week-2 + month-2 milestones. Pure
  // client-side trigger from user.date_joined — fires the dialog
  // 2s after dashboard mount when the operator hits the window AND
  // hasn't already answered or dismissed.
  const nps = useNpsPrompt(user);

  // Show the getting-started guide whenever the user has zero matches —
  // not just on first load. Replaces the dismiss-once localStorage flag
  // with data-driven visibility. User scores one match → guide goes
  // away on next dashboard visit, automatically.
  const isFirstTimeUser = matchCount === 0;
  const onboardingKey = user?.id ? `zcd_dashboard_dismissed_${user.id}` : null;
  const [dismissed, setDismissed] = useState(false);
  // Videos contact modal — opened from the new Resources tile that
  // replaced "Usage & quotas". Videos aren't self-serve yet; users
  // contact admin for access.
  const [videosContactOpen, setVideosContactOpen] = useState(false);
  const videosPanelRef = useRef(null);
  useDialogDismiss({
    open: videosContactOpen,
    onClose: () => setVideosContactOpen(false),
    ref: videosPanelRef,
  });
  useEffect(() => {
    if (!onboardingKey) return;
    try { setDismissed(localStorage.getItem(onboardingKey) === "1"); }
    catch { setDismissed(false); }
  }, [onboardingKey]);
  const showWelcome = isFirstTimeUser && !dismissed;
  const dismissWelcome = () => {
    if (onboardingKey) {
      try { localStorage.setItem(onboardingKey, "1"); } catch { /* ignore */ }
    }
    setDismissed(true);
  };

  if (loading) return null;

  const isOrg = user?.account_type === "organization" || user?.account_type_v2 === "B2B";
  const orgName = user?.organization?.name || user?.organization_name || null;
  const v2Role = user?.account_user_role; // SUPER_ADMIN / ADMIN / USER (B2B only)
  const legacyRole = user?.role;          // OWNER / ADMIN / MEMBER
  const canManageOrg =
    user?.is_superuser ||
    ["SUPER_ADMIN", "ADMIN"].includes(v2Role) ||
    ["OWNER", "ADMIN"].includes(legacyRole);
  // Founder-admin (superuser) doesn't see the customer-side tiles
  // (Subscription, Zencredits, Public profile, Player profile) — they
  // operate the platform, not consume it.
  const isAdmin = !!user?.is_superuser;

  // Action tiles — built per-user based on permissions/account-type
  const tiles = [
    {
      title: "Score & manage matches",
      desc: "Match list, scoring, scorecards, ball-by-ball",
      to: "/cricket/matches",
      accent: "emerald",
      always: true,
    },
    {
      title: "Players",
      desc: isOrg
        ? "Master player database, profiles, edit metadata"
        : "Your players — added as you score matches",
      to: "/cricket/players",
      accent: "blue",
      always: true,
    },
    {
      title: "Teams",
      desc: isOrg
        ? "Team master DB and squads"
        : "Your teams — added as you score matches",
      to: "/cricket/teams",
      accent: "blue",
      always: true,
    },
    {
      title: "Competitions",
      desc: isOrg
        ? "Tournaments, leagues, series"
        : "Your tournaments, leagues, series",
      to: "/cricket/competitions",
      accent: "blue",
      always: true,
    },
    {
      title: "Stadiums & umpires",
      desc: "Venue and officials master data",
      to: "/cricket/stadiums",
      accent: "slate",
      always: true,
    },
    {
      title: "Team & roles",
      desc: "Invite members, change roles, resend or revoke invites",
      to: "/organisation-dashboard",
      accent: "purple",
      featured: true,
      show: isOrg && canManageOrg,
    },
    {
      title: "Subscription",
      desc: mySubscription
        ? `${mySubscription.plan?.name ?? "Free"} · ${mySubscription.status ?? "—"}`
        : "Choose a plan",
      to: "/subscriptions",
      accent: "amber",
      show: !isAdmin,
    },
    {
      title: "Zencredits",
      desc:
        walletBalance?.balance_credits != null
          ? `${walletBalance.balance_credits} ZC available · top up`
          : "Wallet · top up",
      to: "/account/wallet",
      accent: "emerald",
      featured: !isOrg,
      show: !isAdmin,
    },
    {
      title: "Marketplace",
      desc: "Tournament squad packs — skip 2 hours of player entry. Pay in ZC.",
      to: "/marketplace",
      accent: "purple",
      featured: !isOrg,
      always: true,
    },
    {
      // T247.J — discoverability. Public scorer-for-hire profile.
      // Visible to all scorers; non-Elite users hit an upgrade prompt
      // inside the page if they try to submit.
      title: "Public profile",
      desc: "Your scorer-for-hire listing on the Find-a-Scorer marketplace · Elite plan",
      to: "/account/portfolio",
      accent: "purple",
      show: !isOrg && !isAdmin,
    },
    {
      // T265 — player marketplace discoverability.
      title: "Player profile",
      desc: "Your public cricketer profile on Find-a-Player · be findable by scouts + coaches",
      to: "/account/player-profile",
      accent: "purple",
      show: !isOrg && !isAdmin,
    },
    {
      title: "AI guide",
      desc: "AI Tag · AI Suggest · Scorer Chat · voice scoring — features, pricing in Zencredits, what's sent to the model",
      to: "/help/ai-guide",
      accent: "purple",
      always: true,
    },
    {
      title: "User guide",
      desc: "Complete reference — every button, chip, modal & screen explained with live UI mock-ups",
      to: "/help/user-guide",
      accent: "blue",
      always: true,
    },
    {
      title: "API tokens",
      desc: "Generate keys for programmatic access",
      to: "/cricket/api-tokens",
      accent: "slate",
      // Developer feature — hide from B2C / Solo accounts. Org accounts
      // (B2B) typically have devs who'd integrate with the API.
      show: isOrg,
    },
    {
      title: "Usage",
      desc: "API calls, storage, plan limits",
      to: "/cricket/usage",
      accent: "slate",
      // Same rationale — Usage is mostly developer-facing API stats.
      // B2C scorers don't see API call counts.
      show: isOrg,
    },
    {
      // DPDP Act 2023 — Right to Access + Right to Erasure. Always visible
      // so users can find data-export + account-delete without searching.
      title: "Privacy & data",
      desc: "Download your data · Delete account",
      to: "/account/privacy",
      accent: "slate",
      always: true,
    },
    {
      // Single owner-only entry point. Lands on the SPA Owner Console
      // which is the canonical admin surface; the founder pivots from
      // there to ingest / Django / zen-admin as needed.
      title: "Owner Console",
      desc: "Operational admin — KPIs, marketplace, finances, ingest, users.",
      to: "/admin",
      accent: "rose",
      show: !!user?.is_superuser,
    },
  ].filter((t) => t.always || t.show);

  const accentBorder = {
    emerald: "hover:border-emerald-500/60",
    blue:    "hover:border-blue-500/60",
    purple:  "hover:border-purple-500/60",
    amber:   "hover:border-amber-500/60",
    rose:    "hover:border-rose-500/60",
    slate:   "hover:border-slate-500/60",
  };
  const accentText = {
    emerald: "text-emerald-400",
    blue:    "text-blue-400",
    purple:  "text-purple-400",
    amber:   "text-amber-400",
    rose:    "text-rose-400",
    slate:   "text-slate-400",
  };

  const Tile = ({ tile }) => {
    const cls = `block bg-bg-primary border border-bg-secondary ${accentBorder[tile.accent]} rounded-xl p-5 transition group ${tile.featured ? "md:col-span-2" : ""}`;
    const inner = (
      <>
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-base font-semibold text-text-primary">{tile.title}</h3>
          <span className={`${accentText[tile.accent]} text-lg group-hover:translate-x-0.5 transition`}>→</span>
        </div>
        <p className="text-text-secondary text-sm">{tile.desc}</p>
      </>
    );
    return tile.external ? (
      <a href={tile.to} target="_blank" rel="noreferrer" className={cls}>{inner}</a>
    ) : (
      <Link to={tile.to} className={cls}>{inner}</Link>
    );
  };

  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.username || "there";

  const firstName = user?.first_name || displayName;
  const planSlug = mySubscription?.plan?.slug || mySubscription?.plan?.code;
  // "Free plan" surface state: trial OR expired/lapsed. The legacy B2C_FREE
  // plan was deprecated in pricing v2 — trial is now the only no-cost path.
  const onFreePlan = (
    !mySubscription ||
    planSlug === "TRIAL" ||
    mySubscription?.status !== "active"
  );

  // 3-step quick-start tailored by account type / role / plan state
  const quickSteps = (() => {
    if (isOrg && canManageOrg) {
      return [
        {
          n: 1,
          title: "Pick your plan",
          desc: onFreePlan
            ? "Choose Basic or Enterprise to unlock the team seats and full data access."
            : "Plan active. You can change tier any time.",
          to: "/subscriptions",
          done: !onFreePlan,
        },
        {
          n: 2,
          title: "Invite your team",
          desc: "Add teammates so they can score matches and use the data.",
          to: "/organisation-dashboard",
          done: false,
        },
        {
          n: 3,
          title: "Create your first match",
          desc: "Set up teams, toss, playing XI — then start ball-by-ball scoring.",
          to: "/cricket/matches",
          done: false,
        },
      ];
    }
    if (isOrg) {
      // B2B member
      return [
        {
          n: 1,
          title: "Open your match list",
          desc: "Find matches assigned to you and start scoring.",
          to: "/cricket/matches",
          done: false,
        },
        {
          n: 2,
          title: "Browse the player database",
          desc: "Master player profiles with career-long stats.",
          to: "/cricket/players",
          done: false,
        },
        {
          n: 3,
          title: "Visit your organisation",
          desc: "See your role, teammates, and seat usage.",
          to: "/organisation-dashboard",
          done: false,
        },
      ];
    }
    // B2C
    return [
      {
        n: 1,
        title: "Pick your plan",
        desc: onFreePlan
          ? "Trial gives you 30 days + 100 credits. Pick Basic, Intermediate or Advanced to keep scoring after."
          : `${mySubscription?.plan?.name || "Paid"} plan active.`,
        to: "/subscriptions",
        done: !onFreePlan,
      },
      {
        n: 2,
        title: "Score your first match",
        desc: "Create a match, set toss + playing XI, then ball-by-ball scoring.",
        to: "/cricket/matches",
        done: false,
      },
      {
        n: 3,
        title: isOrg ? "Explore the master database" : "Build your roster",
        desc: isOrg
          ? "8,300+ players, 890+ teams, 510+ stadiums to draw from."
          : "Players, teams and stadiums get added to your library as you score matches.",
        to: "/cricket/players",
        done: false,
      },
    ];
  })();

  return (
    <div className="min-h-screen bg-bg-main py-6 sm:py-10 px-3 sm:px-4">
      {/* T211: NPS auto-prompt — only renders when the user hits a
          milestone window (14-day or 60-day) and hasn't already
          answered/dismissed. Dialog handles its own dismiss + submit. */}
      <NpsDialog
        milestone={nps.milestone}
        onClose={nps.dismiss}
        onSubmitted={nps.markDone}
      />
      <div className="max-w-6xl mx-auto">
        {/* Identity strip */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-text-secondary mb-1">
              {isOrg ? "Organisation account" : "Solo account"}
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-text-primary">
              Welcome back, {displayName}
            </h1>
            {orgName && (
              <p className="text-text-secondary text-sm mt-1">
                {orgName}
                {v2Role && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded bg-bg-primary border border-bg-secondary text-text-primary">
                    {v2Role}
                  </span>
                )}
                {!v2Role && legacyRole && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded bg-bg-primary border border-bg-secondary text-text-primary">
                    {legacyRole}
                  </span>
                )}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs">
            {user?.email_verified ? (
              <span className="px-2 py-1 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                Email verified
              </span>
            ) : (
              <UnverifiedEmailBadge email={user?.email} />
            )}
            {user?.is_superuser && (
              <Link
                to="/admin"
                className="px-2 py-1 rounded bg-rose-500/15 text-rose-400 border border-rose-500/30 hover:bg-rose-500/25 transition"
              >
                Owner Console →
              </Link>
            )}
          </div>
        </div>

        {/* First-run welcome panel */}
        {showWelcome && (
          <div className="mb-6 bg-gradient-to-br from-emerald-500/10 via-bg-primary to-bg-primary border border-emerald-500/30 rounded-xl p-5 md:p-6 relative">
            <button
              onClick={dismissWelcome}
              className="absolute top-3 right-3 text-text-secondary hover:text-text-primary text-xs px-2 py-1 rounded border border-bg-secondary hover:border-bg-secondary/80 transition"
              title="Dismiss"
            >
              Dismiss ✕
            </button>
            <div className="flex items-start gap-3 mb-4 pr-20">
              <div className="text-2xl">👋</div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-text-primary">
                  Welcome to ZenCricketData, {firstName}!
                </h2>
                <p className="text-text-secondary text-sm mt-1">
                  {isOrg && canManageOrg
                    ? `You're set up as ${v2Role || legacyRole || "admin"} of ${orgName || "your organisation"}. Here's how to get your team rolling:`
                    : isOrg
                    ? `You're a member of ${orgName || "your organisation"}. Here's how to get started:`
                    : "Let's get you to your first scored ball. Three quick steps:"}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {quickSteps.map((step) => (
                <Link
                  key={step.n}
                  to={step.to}
                  className={`group block bg-bg-primary border rounded-lg p-4 transition ${
                    step.done
                      ? "border-emerald-500/40 opacity-70"
                      : "border-bg-secondary hover:border-emerald-500/60"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                        step.done
                          ? "bg-emerald-500/30 text-emerald-300"
                          : "bg-emerald-500/15 text-emerald-400"
                      }`}
                    >
                      {step.done ? "✓" : step.n}
                    </span>
                    <h3 className="text-sm font-semibold text-text-primary">{step.title}</h3>
                    <span className="ml-auto text-emerald-400 text-sm group-hover:translate-x-0.5 transition">→</span>
                  </div>
                  <p className="text-text-secondary text-xs leading-relaxed">{step.desc}</p>
                </Link>
              ))}
            </div>
            <p className="text-text-secondary text-xs mt-4">
              Stuck anywhere? See the{" "}
              <Link to="/help/user-guide" className="text-emerald-400 hover:underline">user guide</Link>
              {" "}or contact <span className="text-text-primary">admin</span>.
            </p>
          </div>
        )}

        {/* Restore the welcome panel on demand if dismissed */}
        {!showWelcome && (
          <div className="mb-4 flex justify-end">
            <button
              onClick={() => setShowWelcome(true)}
              className="text-xs text-text-secondary hover:text-text-primary px-2 py-1 rounded border border-bg-secondary hover:border-emerald-500/50 transition"
            >
              Show quick start
            </button>
          </div>
        )}

        {/* T205: Persona-1 scorer hero card. Renders for non-org
            users who have at least one match. Shows: resume CTA for
            the most recent active match, today's match count, and
            a 7-day scoring streak. Hidden for B2B (org dashboard)
            and for first-time users (let the onboarding guide
            handle them). */}
        {!isOrg && matchCount && matchCount > 0 && (
          <ScorerHeroCard
            matches={recentMatches || []}
            walletBalance={walletBalance}
          />
        )}

        {/* Action tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {tiles.map((t) => <Tile key={t.title} tile={t} />)}
        </div>

        {/* Compact subscription summary footer */}
        <div className="mt-10 bg-bg-primary border border-bg-secondary rounded-xl p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-text-secondary mb-1">
                Subscription
              </p>
              <p className="text-text-primary font-medium">
                {mySubscription?.plan?.name ?? "No active plan"}
                <span className="ml-2 text-text-secondary text-sm">
                  · {mySubscription?.plan?.billing_cycle ?? "—"}
                </span>
                <span
                  className={`ml-2 px-2 py-0.5 text-xs rounded border ${
                    mySubscription?.status === "active"
                      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                      : "bg-bg-secondary text-text-secondary border-bg-secondary"
                  }`}
                >
                  {mySubscription?.status ?? "inactive"}
                </span>
              </p>
              {mySubscription?.current_period_end && (
                <p className="text-text-secondary text-xs mt-1">
                  Valid until {mySubscription.current_period_end}
                </p>
              )}
              {mySubscription?.pending_plan && (
                <p className="text-amber-400 text-xs mt-1">
                  Pending change → {mySubscription.pending_plan.name}
                </p>
              )}
            </div>
            <Link
              to="/subscriptions"
              className="px-4 py-2 bg-bg-secondary hover:bg-button-primary/30 text-text-primary text-sm rounded-lg border border-bg-secondary transition"
            >
              Manage subscription
            </Link>
          </div>
        </div>

        {/* Resources strip — always visible */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <Link
            to="/help/user-guide"
            className="bg-bg-primary border border-bg-secondary hover:border-blue-500/40 rounded-lg p-4 transition"
          >
            <p className="text-xs uppercase tracking-wider text-text-secondary mb-1">Help</p>
            <p className="text-text-primary text-sm font-medium">User guide</p>
            <p className="text-text-secondary text-xs">Every feature, button & modal explained</p>
          </Link>
          <Link
            to="/help/ai-guide"
            className="bg-bg-primary border border-bg-secondary hover:border-purple-500/40 rounded-lg p-4 transition"
          >
            <p className="text-xs uppercase tracking-wider text-text-secondary mb-1">AI</p>
            <p className="text-text-primary text-sm font-medium">AI guide</p>
            <p className="text-text-secondary text-xs">AI Tag, Suggest, Scorer Chat & Zencredits</p>
          </Link>
          {/* Videos — replaces "Usage & quotas" (2026-06-06 founder ask).
              Videos aren't self-serve yet; opens a contact modal with
              admin email + WhatsApp so the customer can request access. */}
          <button
            type="button"
            onClick={() => setVideosContactOpen(true)}
            className="bg-bg-primary border border-bg-secondary hover:border-emerald-500/40 rounded-lg p-4 transition text-left"
          >
            <p className="text-xs uppercase tracking-wider text-text-secondary mb-1">Library</p>
            <p className="text-text-primary text-sm font-medium">Videos</p>
            <p className="text-text-secondary text-xs">Contact admin for videos</p>
          </button>
          {/* Contact us — both email and WhatsApp surfaced inline so
              the customer can pick the channel they prefer. WhatsApp
              link goes to the Meta-approved Twilio OTP sender. */}
          <div className="bg-bg-primary border border-bg-secondary hover:border-amber-500/40 rounded-lg p-4 transition">
            <p className="text-xs uppercase tracking-wider text-text-secondary mb-1">Support</p>
            <p className="text-text-primary text-sm font-medium mb-1.5">Contact us</p>
            <div className="flex flex-col gap-1">
              <a
                href="mailto:admin@zencricketdata.com"
                className="text-text-secondary text-xs hover:text-text-primary inline-flex items-center gap-1.5"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 5L2 7"/></svg>
                admin@zencricketdata.com
              </a>
              <a
                href="https://wa.me/15559692546"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-300 text-xs hover:text-emerald-200 inline-flex items-center gap-1.5"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.695.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413"/></svg>
                +1 555 969 2546 (WhatsApp)
              </a>
            </div>
          </div>
        </div>
        {/* Videos contact modal (2026-06-06 founder ask). Triggered
            from the Videos tile that replaced "Usage & quotas". */}
        {videosContactOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            role="presentation"
            onClick={() => setVideosContactOpen(false)}
          >
            <div
              ref={videosPanelRef}
              role="dialog"
              aria-modal="true"
              aria-label="Videos library"
              className="bg-bg-primary border border-bg-secondary rounded-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-text-secondary mb-1">Library</p>
                  <h3 className="text-text-primary text-lg font-semibold">Videos</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setVideosContactOpen(false)}
                  className="text-text-secondary hover:text-text-primary text-xl leading-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <p className="text-text-primary text-sm mb-4">Contact admin for videos.</p>
              <div className="space-y-2">
                <a
                  href="mailto:admin@zencricketdata.com"
                  className="flex items-center gap-2 px-3 py-2 rounded border border-bg-secondary hover:border-amber-500/40 text-sm text-text-primary"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 5L2 7"/></svg>
                  admin@zencricketdata.com
                </a>
                <a
                  href="https://wa.me/15559692546"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 rounded border border-bg-secondary hover:border-emerald-500/40 text-sm text-emerald-300"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.695.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413"/></svg>
                  +1 555 969 2546 (WhatsApp)
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// T205: Persona-1 scorer hero card. Renders above the action tiles
// for B2C scorers with at least one match. Surfaces the three things
// a returning scorer actually wants: their active match (resume CTA),
// what they've done today, and their continuity streak. Wallet
// balance is a useful at-a-glance pill since voice + AI fall back to
// credits if they exhaust their plan quota.
//
// All numbers are derived from the latest 20 matches the caller
// already fetched — no extra API calls. Compromises are intentional:
//   - Streak uses match.created_at not actual ball saves. Good enough
//     for the persona-1 use case; precise via /api/analytics/event/
//     later when we have first_ball_saved instrumented (T212).
//   - "Today" is local timezone of the operator's browser.
function ScorerHeroCard({ matches, walletBalance }) {
  if (!matches || matches.length === 0) return null;

  // Find the most recently CODED IN_PROGRESS / SETUP / TOSS_DONE
  // match — operator wants to resume the one they were last bowling
  // on, not the one they most recently CREATED. Sort resumable
  // matches by last_ball_at (server-derived from the latest ball's
  // created_at) falling back to created_at, then take the freshest.
  const RESUMABLE_STATES = new Set([
    "IN_PROGRESS", "SETUP", "TOSS_DONE", "READY", "ACTIVE",
  ]);
  const resumable = matches.filter((m) => RESUMABLE_STATES.has(m.state));
  resumable.sort((a, b) => {
    const ta = new Date(a.last_ball_at || a.updated_at || a.created_at || 0).getTime();
    const tb = new Date(b.last_ball_at || b.updated_at || b.created_at || 0).getTime();
    return tb - ta;
  });
  const resumeTarget = resumable[0];

  // Compose a human "Team A vs Team B" label. Ladder: explicit
  // match_name → short-name pair → long-name pair → 8-char id slug
  // as last resort. Pre-2026-06-04 the tile fell straight to the slug
  // because match_name / short_name didn't exist on the API response —
  // that's Keerthi's "Match #ea836bb2" bug. The serializer now ships
  // team1_name / team2_name plus the short-name pair on every row.
  const labelForMatch = (m) => {
    if (!m) return "";
    if (m.match_name) return m.match_name;
    if (m.short_name) return m.short_name;
    const t1 = m.team1_short_name || m.team1_name;
    const t2 = m.team2_short_name || m.team2_name;
    if (t1 && t2) return `${t1} vs ${t2}`;
    if (t1 || t2) return t1 || t2;
    const slug = (m.id || "").slice?.(0, 8) || "";
    return slug ? `Match #${slug}` : "Match";
  };

  // Today count — matches created or updated today (operator's
  // local time).
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayCount = matches.filter((m) => {
    const ts = new Date(m.created_at || m.updated_at || 0);
    return ts >= today;
  }).length;

  // 7-day streak — distinct dates in the last 7 days where ≥ 1
  // match was created. Bounded by 7 so we never claim a longer
  // streak than the slice we read.
  const days = new Set();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dStr = d.toISOString().slice(0, 10);
    const hit = matches.some((m) => {
      const ts = new Date(m.created_at || m.updated_at || 0);
      return ts.toISOString().slice(0, 10) === dStr;
    });
    if (hit) days.add(dStr);
    else if (i > 0) break;   // streak broken once a gap appears
  }
  const streak = days.size;

  const credits = walletBalance?.balance_credits ?? null;

  return (
    <div className="mb-4 bg-gradient-to-br from-emerald-500/15 via-bg-primary to-purple-500/10 border border-emerald-500/30 rounded-xl p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1">
            Scorer dashboard
          </p>
          {resumeTarget ? (
            <h2 className="text-lg sm:text-xl font-bold text-text-primary">
              Resume: <span className="text-emerald-300 truncate">
                {labelForMatch(resumeTarget)}
              </span>
            </h2>
          ) : (
            <h2 className="text-lg sm:text-xl font-bold text-text-primary">
              Ready when you are — start a new match
            </h2>
          )}
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
            <span className="inline-flex items-center gap-1 text-text-secondary">
              <span className="font-mono text-text-primary tabular-nums">{todayCount}</span>
              match{todayCount === 1 ? "" : "es"} today
            </span>
            <span className="inline-flex items-center gap-1 text-text-secondary">
              <span className="font-mono text-text-primary tabular-nums">{streak}</span>
              day streak {streak >= 3 ? "🔥" : ""}
            </span>
            {credits != null && (
              <span className="inline-flex items-center gap-1 text-text-secondary">
                <span className="font-mono text-text-primary tabular-nums">{credits}</span>
                Zencredits
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {resumeTarget ? (
            <Link
              to={`/ball/scoring/${resumeTarget.id}`}
              className="px-4 py-2 rounded-lg bg-emerald-500 text-bg-main font-semibold text-sm hover:bg-emerald-400 shadow-lg shadow-emerald-500/20"
            >
              Resume →
            </Link>
          ) : (
            <Link
              to="/cricket/matches/add"
              className="px-4 py-2 rounded-lg bg-emerald-500 text-bg-main font-semibold text-sm hover:bg-emerald-400 shadow-lg shadow-emerald-500/20"
            >
              New match →
            </Link>
          )}
          <Link
            to="/cricket/matches"
            className="px-3 py-2 rounded-lg bg-bg-secondary border border-border-default text-text-primary font-medium text-sm hover:bg-bg-main"
          >
            All matches
          </Link>
        </div>
      </div>
    </div>
  );
}
